import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSSE } from '@/hooks/sse/useSSE';
import { useSnackbar } from '@/hooks/global-tools';
import type { AiChatMessage } from '@/components/@IntranetLibrary/AiChatView';
import type {
  ApprovalChatAnswerCompletePayload,
  ApprovalChatAnswerErrorPayload,
  ApprovalChatSuggestion,
} from '@/types/approvalChat';
import {
  applyAnswer,
  applyError,
  endsWithUnanswered,
  isServerMessageId,
  latestSessionId,
  mapHistory,
  resolveSuggestion,
  type ApprovalChatAssistantMeta,
  type SuggestionState,
} from '@/utils/approval/approvalChat';
import {
  getApprovalChatMessages,
  patchApprovalChatSuggestion,
  postApprovalChat,
  useApprovalChatSessions,
} from './approvalChatApi';

const HISTORY_LIMIT = 50;

// 답변 대기 복원 폴링: 답변 생성 중 패널을 닫으면 SSE 구독이 끊긴다. 다시 열면 topic 은 sessionId 로
// 알 수 있지만 이미 발행된 이벤트는 다시 오지 않으므로, 이력을 주기적으로 읽어 답변을 받는다.
const PENDING_POLL_INTERVAL_MS = 3000;
const PENDING_POLL_TIMEOUT_MS = 3 * 60 * 1000;
const RESTORED_PENDING_ID = 'tmp-restored-pending';

const pendingMeta = (aiPipelineId: string): ApprovalChatAssistantMeta => ({
  aiPipelineId,
  suggestion: null,
});

/**
 * 결재 AI 어시스턴트 채팅 상태 훅 (PORTAL-15498). 회의록 useTalkFlowChat 과 같은 흐름.
 *
 * - 진입 시 세션목록의 최신 세션 1개를 복원해 이력 로드.
 * - 전송: POST …/chat → 202 {sessionId, topic, aiPipelineId} → 낙관적 말풍선.
 *   답변은 SSE(topic `approval-chat:{sessionId}`, event `approval.chat.answer.*`)로 aiPipelineId 매칭해 채운다.
 *   완료 payload 에는 서버가 분리한 반려 제안(suggestion)이 함께 온다.
 */
const useApprovalChat = (documentId: string) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const {
    data: sessions,
    error: sessionsError,
    mutate: mutateSessions,
  } = useApprovalChatSessions(documentId);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const idRef = useRef(0);
  const nextId = () => `tmp-${(idRef.current += 1)}`;
  const restoredRef = useRef(false);
  const pendingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPendingPoll = useCallback(() => {
    if (pendingPollRef.current) {
      clearInterval(pendingPollRef.current);
      pendingPollRef.current = null;
    }
  }, []);

  const startPendingPoll = useCallback(
    (session: string) => {
      stopPendingPoll();
      setMessages((prev) => [
        ...prev,
        {
          kind: 'assistant',
          id: RESTORED_PENDING_ID,
          text: '',
          status: 'pending',
          meta: pendingMeta(''),
        },
      ]);
      setIsSending(true);

      const startedAt = Date.now();
      pendingPollRef.current = setInterval(async () => {
        try {
          const list = await getApprovalChatMessages(documentId, session, {
            limit: HISTORY_LIMIT,
          });
          const mapped = mapHistory(list ?? []);
          if (endsWithUnanswered(mapped)) {
            if (Date.now() - startedAt < PENDING_POLL_TIMEOUT_MS) return;
            stopPendingPoll();
            setMessages((prev) =>
              prev.map((m) =>
                m.id === RESTORED_PENDING_ID && m.kind === 'assistant'
                  ? { ...m, status: 'error' as const }
                  : m,
              ),
            );
            setIsSending(false);
            return;
          }
          stopPendingPoll();
          const last = mapped[mapped.length - 1];
          setMessages(
            mapped.map((m) =>
              m === last && m.kind === 'assistant'
                ? { ...m, isAnimated: true }
                : m,
            ),
          );
          setHasMore((list?.length ?? 0) >= HISTORY_LIMIT);
          setIsSending(false);
        } catch {
          // 무시 — 다음 주기에 재시도
        }
      }, PENDING_POLL_INTERVAL_MS);
    },
    [documentId, stopPendingPoll],
  );

  useEffect(() => stopPendingPoll, [stopPendingPoll]);

  // documentId 변경 시 초기화
  useEffect(() => {
    stopPendingPoll();
    restoredRef.current = false;
    setSessionId(null);
    setActiveTopic(null);
    setMessages([]);
    setIsSending(false);
    setIsInitializing(true);
    setHasMore(false);
    setIsLoadingMore(false);
  }, [documentId, stopPendingPoll]);

  // 최초 진입: 최신 세션 복원 + 이력 로드 (한 번만)
  useEffect(() => {
    if (restoredRef.current) return;
    if (sessionsError) {
      restoredRef.current = true;
      setIsInitializing(false);
      return;
    }
    if (!sessions) return;

    restoredRef.current = true;

    const latest = latestSessionId(sessions);
    if (!latest) {
      setIsInitializing(false);
      return;
    }

    setSessionId(latest);
    setActiveTopic(`approval-chat:${latest}`);
    getApprovalChatMessages(documentId, latest, { limit: HISTORY_LIMIT })
      .then((list) => {
        const mapped = mapHistory(list ?? []);
        setMessages(mapped);
        setHasMore((list?.length ?? 0) >= HISTORY_LIMIT);
        if (endsWithUnanswered(mapped)) startPendingPoll(latest);
      })
      .catch(() => undefined)
      .finally(() => setIsInitializing(false));
  }, [sessions, sessionsError, documentId, startPendingPoll]);

  // 위로 스크롤 시 과거 메시지 로드 (beforeId 페이지네이션)
  const loadMore = useCallback(async () => {
    if (!sessionId || isLoadingMore || !hasMore) return;
    const oldest = messages.find(
      (m) => !m.id.startsWith('tmp-') && !m.id.startsWith('local-'),
    );
    if (!oldest) return;

    setIsLoadingMore(true);
    try {
      const older = await getApprovalChatMessages(documentId, sessionId, {
        limit: HISTORY_LIMIT,
        beforeId: oldest.id,
      });
      setMessages((prev) => [...mapHistory(older ?? []), ...prev]);
      setHasMore((older?.length ?? 0) >= HISTORY_LIMIT);
    } catch {
      // 무시 — 다음 스크롤에서 재시도
    } finally {
      setIsLoadingMore(false);
    }
  }, [sessionId, documentId, isLoadingMore, hasMore, messages]);

  // 실시간 답변 수신 — 세션당 1회 구독
  const handlers = useMemo(
    () => ({
      'approval.chat.answer.complete': (data: {
        payload?: ApprovalChatAnswerCompletePayload;
      }) => {
        const payload = data?.payload;
        if (!payload?.aiPipelineId) return;
        setMessages((prev) =>
          applyAnswer(
            prev,
            payload.aiPipelineId as string,
            payload.answer ?? '',
            payload.suggestion ?? null,
            payload.messageId,
          ),
        );
        setIsSending(false);
      },
      'approval.chat.answer.error': (data: {
        payload?: ApprovalChatAnswerErrorPayload;
      }) => {
        const aiPipelineId = data?.payload?.aiPipelineId;
        if (!aiPipelineId) return;
        setMessages((prev) => applyError(prev, aiPipelineId));
        setIsSending(false);
      },
    }),
    [],
  );

  useSSE(activeTopic ? [activeTopic] : [], handlers, !!activeTopic);

  const send = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || isSending) return;

      const assistantId = nextId();
      setMessages((prev) => [
        ...prev,
        { kind: 'user', id: nextId(), text, isAnimated: true },
        {
          kind: 'assistant',
          id: assistantId,
          text: '',
          status: 'pending',
          isAnimated: true,
          meta: pendingMeta(''),
        },
      ]);
      setIsSending(true);

      try {
        const { data } = await postApprovalChat(documentId, {
          message: text,
          sessionId,
        });
        if (!sessionId) {
          setSessionId(data.sessionId);
          // 첫 질문으로 세션이 생김 → 세션목록 캐시 갱신. 안 하면 패널을 닫았다 열 때 stale [] 를 읽어
          // 방금 만든 세션을 못 잡고 또 새 세션으로 시작해 이력이 갈라진다.
          mutateSessions();
        }
        setActiveTopic(data.topic);
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && m.kind === 'assistant'
              ? { ...m, meta: pendingMeta(data.aiPipelineId) }
              : m,
          ),
        );
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId && m.kind === 'assistant'
              ? { ...m, status: 'error' as const }
              : m,
          ),
        );
        setIsSending(false);
        showError({ message: t('메시지 전송에 실패했습니다.') });
      }
    },
    [documentId, sessionId, isSending, mutateSessions, showError, t],
  );

  // 진행 중 답변 중단 — 취소 API 가 없어 로컬에서만 대기 해제 + pending 말풍선 제거.
  const stop = useCallback(() => {
    if (!isSending) return;
    stopPendingPoll();
    setMessages((prev) =>
      prev.filter((m) => !(m.kind === 'assistant' && m.status === 'pending')),
    );
    setIsSending(false);
  }, [isSending, stopPendingPoll]);

  // 채팅 초기화 — 세션 연결을 끊고 화면을 비운다. 다음 send 가 sessionId 없이 POST 하면 새 세션.
  const resetChat = useCallback(() => {
    stopPendingPoll();
    setSessionId(null);
    setActiveTopic(null);
    setMessages([]);
    setIsSending(false);
    setHasMore(false);
    setIsLoadingMore(false);
  }, [stopPendingPoll]);

  /**
   * 제안 카드 응답. 수락/거절 모두 대화에 사용자 답과 어시스턴트 결과를 로컬 말풍선으로 남기고(결재 결과는 문서
   * 자체에 보인다), 응답 상태는 서버 메시지에 기록해 패널을 다시 열어도 카드가 「처리됨」으로 복원되게 한다.
   */
  const answerSuggestion = useCallback(
    (
      messageId: string,
      state: Exclude<SuggestionState, 'open'>,
      reply: { user?: string; assistant: string; doneLabel?: string },
    ) => {
      if (sessionId && isServerMessageId(messageId)) {
        // 기록 실패는 화면 흐름에 영향 없음 — 다음에 열면 카드가 다시 열린 상태로 보일 뿐
        patchApprovalChatSuggestion(
          documentId,
          sessionId,
          messageId,
          state,
        ).catch(() => undefined);
      }
      setMessages((prev) => [
        ...resolveSuggestion(prev, messageId, state),
        ...(reply.user
          ? [
              {
                kind: 'user' as const,
                id: `local-${nextId()}`,
                text: reply.user,
                isAnimated: true,
              },
            ]
          : []),
        {
          kind: 'assistant',
          id: `local-${nextId()}`,
          text: reply.assistant,
          status: 'done',
          isAnimated: true,
          meta: {
            aiPipelineId: '',
            suggestion: null,
            doneLabel: reply.doneLabel,
          } satisfies ApprovalChatAssistantMeta,
        },
      ]);
    },
    [documentId, sessionId],
  );

  return {
    messages,
    isSending,
    isInitializing,
    send,
    stop,
    resetChat,
    loadMore,
    hasMore,
    isLoadingMore,
    answerSuggestion,
  };
};

export type { ApprovalChatSuggestion };
export default useApprovalChat;
